import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Filter, Inbox } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { CalendarFilterSheet } from '../components/CalendarFilterSheet';
import { CalendarInbox } from '../components/CalendarInbox';
import { CalendarToolbar } from '../components/CalendarToolbar';
import { DayAgendaList } from '../components/DayAgendaList';
import { DayContextMenu } from '../components/DayContextMenu';
import { EventSheet } from '../components/EventSheet';
import { ProjectCalendar, type CalendarControls } from '../components/ProjectCalendar';
import { useCalendarInbox } from '../hooks/useCalendarInbox';
import { useCalendarItems } from '../hooks/useCalendarItems';
import { churchToday } from '../lib/adapters';
import { itemsOfDay } from '../lib/dayItems';
import { applyFilters, countActiveFilters, filterOptionsFrom } from '../lib/filter';
import { calendarRange, readPosition, writePosition, type CalendarView } from '../lib/viewState';
import { useAuth } from '@/app/providers/AuthProvider';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingState } from '@/shared/components/LoadingState';
import { Sheet } from '@/shared/components/ui/sheet';
import { can } from '@/shared/constants/roles';
import { isProjectStatus } from '@/shared/constants/statuses';
import { messages } from '@/shared/constants/messages';
import { formatMonthYear } from '@/shared/lib/dates';
import { toUserMessage } from '@/shared/lib/errors';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { useReplayAnimation } from '@/shared/hooks/useReplayAnimation';
import { cn } from '@/shared/lib/utils';
import type { CalendarFilters, CalendarItem } from '@/shared/types/domain';

/**
 * `/calendario` — **uma grade, e tudo nela é a agenda da igreja**
 * (spec-calendar-ux.md, [ADR-015](../../../../docs/adr/ADR-015-agenda-volatil.md)).
 *
 * ⚠️ **Não existe mais lista de calendários.** A tela deixou de perguntar *"quais
 * calendários você quer ver?"* — pergunta que só faz sentido onde os eventos vêm
 * de contas diferentes que são suas — e passa a mostrar **tudo que está vivo, de
 * todo mundo, na filial**. O que separa uma coisa da outra não é uma caixa de
 * seleção: é **cor** (quão firme é), **preenchimento** (se é seu) e **filtro** (o
 * recorte que você pediu).
 *
 * A premissa que autoriza isso não é dedutível do código, e está na ADR-015: **a
 * agenda da igreja é volátil, e o encaixe é coletivo.** O calendário é superfície
 * de negociação, não registro de decisões — por isso a proposta de outro
 * ministério aparece, e por isso é aqui que se cria e se retoma projeto.
 *
 * **Duas seções verticais, separadas por um traço de 1px**: à esquerda o que
 * precisa de você e o que acontece no dia escolhido; à direita a grade. Não são
 * dois painéis — nada de cartão, sombra ou fundo distinto. A divisão é de
 * assunto: **o que se faz** de um lado, **o que se consulta** do outro.
 *
 * **Onde cada estado mora**: filtro na URL (é recorte: colável, e voltar desfaz);
 * vista e dia selecionado em memória de módulo (é posição de leitura: sobrevive a
 * ir e voltar de um projeto, morre no F5). Não há mais preferência em
 * `localStorage` — ela existia para as fontes.
 */
export function CalendarPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const roles = currentUser?.roles ?? [];
  const isReviewer = can(roles, 'reviewProject');
  const canWrite = can(roles, 'writeDraft');

  const remembered = useMemo(() => readPosition(), []);
  const today = churchToday();

  const [view, setView] = useState<CalendarView>(remembered.view);
  const [selectedDay, setSelectedDay] = useState(remembered.selectedDay ?? today);
  const [anchor, setAnchor] = useState(remembered.anchor ?? today);
  /** O PERÍODO (agosto inteiro), não a grade visível — é dele que sai o título. */
  const [period, setPeriod] = useState<{ start: string; end: string } | null>(null);
  const [menu, setMenu] = useState<{ day: string; x: number; y: number } | null>(null);
  const [sheetItem, setSheetItem] = useState<CalendarItem | null>(null);
  /**
   * ⚠️ A folha de FILTROS não é mais só do celular (§18): ela é o filtro, em
   * qualquer largura. A de "Precisa de você" continua sendo o desdobramento
   * móvel da coluna da esquerda (§2.2).
   */
  const [sheet, setSheet] = useState<'filters' | 'inbox' | null>(null);
  const grid = useRef<CalendarControls>(null);

  /**
   * O movimento da grade (§9, decisões 87 e 88). **Três trocas, três respostas
   * diferentes — e a quarta é não fazer nada:**
   *
   * | Quando | O quê | Por quê |
   * |---|---|---|
   * | Período (‹ ›, Hoje) | desliza na DIREÇÃO da navegação | é a única das três em que existe direção, e ignorá-la joga fora a informação mais barata da tela |
   * | Vista | `fade`, sem deslocamento | não há "para que lado": a grade muda de anatomia |
   * | Dia selecionado | a coluna faz `rise-in` | "conteúdo chegou" |
   * | **Refetch** | **nada** (88) | a chegada do dado já é o aviso; anunciar também a partida vira tremor |
   *
   * `token` existe porque a mesma animação precisa rodar de novo ao avançar dois
   * meses seguidos — sem ele, a classe não muda e o navegador não reinicia nada.
   */
  const [motion, setMotion] = useState({ className: 'animate-fade-in', token: 0 });
  /**
   * Trocar de vista dispara `changeView`, que dispara `datesSet` — ou seja, o
   * handler de PERÍODO roda logo depois do de vista e sobrescreveria o `fade`
   * com um deslize. Esta marca é o que faz a troca de vista continuar sendo um
   * fade, sem que os dois handlers precisem saber um do outro.
   */
  const viewChanging = useRef(false);
  /** A âncora do período anterior — é dela que sai a DIREÇÃO do deslize. */
  const anchorRef = useRef(remembered.anchor ?? today);

  // A vista Semana em 375px é a mais frágil das três; a de Mês troca chips por
  // marcadores (decisão 59). Uma consulta de mídia, lida uma vez.
  const isNarrow = useMediaQuery('(max-width: 639px)');
  // O mesmo `lg` do Tailwind: acima dele a rota é de altura cheia e a grade
  // herda a altura do painel; abaixo, a página rola (§2.2, §6.2).
  const isWide = useMediaQuery('(min-width: 1024px)');

  // Memória de módulo: é o que faz voltar de um projeto reencontrar o mesmo dia
  // selecionado, e o F5 recair em hoje.
  useEffect(() => {
    writePosition({ view, selectedDay, anchor });
  }, [view, selectedDay, anchor]);

  /**
   * O recorte vive na URL, e cada dimensão é **a chave REPETIDA**
   * (`?ministerio=a&ministerio=b`) — não uma lista separada por vírgula. É o que
   * `URLSearchParams` já sabe ler e escrever, e o que mantém o endereço colável
   * mesmo com um nome de espaço que tenha vírgula dentro.
   */
  const filters = useMemo<CalendarFilters>(() => {
    return {
      coordinationIds: listOf(searchParams.getAll('coordenacao')),
      ministryIds: listOf(searchParams.getAll('ministerio')),
      spaceNames: listOf(searchParams.getAll('espaco')),
      // Parâmetro escrito à mão que não faz sentido é IGNORADO, nunca exibido
      // como erro — o mesmo que `ProjectsPage` já faz.
      statuses: listOf(searchParams.getAll('situacao').filter(isProjectStatus)),
    };
  }, [searchParams]);

  const handleFilters = useCallback(
    (next: CalendarFilters) => {
      const params = new URLSearchParams();
      for (const id of next.coordinationIds ?? []) params.append('coordenacao', id);
      for (const id of next.ministryIds ?? []) params.append('ministerio', id);
      for (const name of next.spaceNames ?? []) params.append('espaco', name);
      for (const status of next.statuses ?? []) params.append('situacao', status);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const range = useMemo(() => calendarRange(anchor), [anchor]);
  const query = useCalendarItems(range, currentUser?.profile.id ?? null);
  const inbox = useCalendarInbox(currentUser?.profile.id ?? null, isReviewer);

  const all = useMemo(() => query.data ?? [], [query.data]);
  const options = useMemo(() => filterOptionsFrom(all), [all]);
  const filtered = useMemo(() => applyFilters(all, filters), [all, filters]);
  const dayItems = useMemo(() => itemsOfDay(filtered, selectedDay), [filtered, selectedDay]);

  /**
   * O título é o MÊS visível (decisão 80), e quem o formata é
   * `shared/lib/dates.ts` — nunca o FullCalendar, cujo título muda de anatomia a
   * cada vista. Antes do primeiro `datesSet` cai na âncora, para a tela nunca
   * pintar sem título.
   */
  const title = useMemo(() => {
    if (!period) return formatMonthYear(`${anchor}T12:00:00-03:00`, `${anchor}T12:00:00-03:00`);
    return formatMonthYear(`${period.start}T12:00:00-03:00`, `${period.end}T00:00:00-03:00`);
  }, [period, anchor]);

  const changeView = useCallback((next: CalendarView) => {
    viewChanging.current = true;
    setMotion((current) => ({ className: 'animate-fade-in', token: current.token + 1 }));
    setView(next);
  }, []);

  const handlePeriodChange = useCallback(
    (info: { anchor: string; periodEnd: string; start: string; end: string }) => {
      // ⚠️ A âncora anterior vem de um `ref`, e não do updater de `setAnchor`:
      // updater de estado tem de ser PURO — em modo estrito o React o chama duas
      // vezes, e um `setMotion` lá dentro contaria dois períodos a cada clique.
      const previous = anchorRef.current;
      anchorRef.current = info.anchor;

      if (viewChanging.current) {
        viewChanging.current = false;
      } else if (info.anchor !== previous) {
        // A comparação é de string `yyyy-mm-dd`, que ordena como data — é o que
        // dá a DIREÇÃO sem `Date` nenhum no caminho.
        setMotion((motionState) => ({
          className: info.anchor > previous ? 'animate-slide-in-next' : 'animate-slide-in-prev',
          token: motionState.token + 1,
        }));
      }

      setAnchor(info.anchor);
      setPeriod({ start: info.anchor, end: info.periodEnd });
      // Navegar para outro período move a seleção para dentro dele (decisão 72):
      // uma lista de um dia que não está na grade seria a tela falando de duas
      // datas ao mesmo tempo. `start` e não `anchor` porque na vista de Semana o
      // período começa num domingo qualquer, não no dia 1º.
      setSelectedDay((current) =>
        current >= info.start && current < info.end ? current : firstVisibleDay(info),
      );
    },
    [],
  );

  /**
   * **Clicar num chip não navega** (decisão 84): abre a folha. O alvo tem 2mm, e
   * navegar dali cobra uma ida e uma volta de quem só queria conferir de que
   * projeto era aquela faixa. Quem navega é "Mostrar".
   */
  const openItem = useCallback((item: CalendarItem) => setSheetItem(item), []);

  /**
   * **Nenhuma rota nova.** `/calendario/:id` está proibida desde a decisão 1 da
   * 1ª rodada da análise; a origem viaja em `state`, e é ela que mantém
   * "Calendário" aceso e produz a trilha `Calendário › Retiro de Jovens`. A
   * leitura reduzida de quem não alcança a linha pela RLS é resolvida na própria
   * `/projetos/:id`.
   */
  const showItem = useCallback(
    (item: CalendarItem) => {
      if (item.kind !== 'project') return;
      setSheetItem(null);
      navigate(`/projetos/${item.project.id}`, { state: { from: '/calendario' } });
    },
    [navigate],
  );

  /**
   * ⚠️ A animação vai numa `div` de INVÓLUCRO, sem classe de duração: o
   * `tailwindcss-animate` emite `duration-*` também como `animation-duration`,
   * e um elemento que já carrega `duration-fast` para o hover encurtaria a
   * entrada para 110ms sem ninguém pedir.
   */
  const gridMotionRef = useReplayAnimation<HTMLDivElement>(motion.className, motion.token);
  const dayMotionRef = useReplayAnimation<HTMLDivElement>('animate-rise-in', selectedDay);

  const inboxData = inbox.data;
  const hasInbox =
    inboxData !== undefined &&
    (inboxData.pending.length > 0 ||
      inboxData.updates.length > 0 ||
      (inboxData.reviewQueueCount ?? 0) > 0);

  return (
    <div className="flex flex-1 flex-col lg:min-h-0 lg:flex-row">
      {/*
        ⚠️ **A grade vem PRIMEIRO no DOM e segunda na tela** (`lg:order-2`), e a
        inversão é deliberada: o `<h1>` da tela mora na barra dela, e um
        documento cujo título aparece depois de duas listas é um documento que
        leitor de tela e busca leem de trás para frente. Abaixo de `lg` a ordem
        do DOM já é a visual — barra → grade → lista (§2.2).
      */}
      <section className="flex min-w-0 flex-1 flex-col gap-3 lg:order-2 lg:min-h-0 lg:border-l lg:border-border lg:pl-5">
        <CalendarToolbar
          title={title}
            view={view}
          onViewChange={changeView}
          onPrev={() => grid.current?.prev()}
          onNext={() => grid.current?.next()}
          onToday={() => {
            grid.current?.today();
            // "Hoje" move a grade E a seleção: mover só a grade deixaria a lista
            // ao lado mostrando o dia de onde a pessoa veio, que é exatamente o
            // que ela pediu para largar.
            setSelectedDay(churchToday());
          }}
          filterButton={
            /* Só o funil, sem a palavra: ele fica colado no mês, e um rótulo ali
               competiria com o título da tela pela mesma linha de leitura. O
               nome acessível continua sendo "Filtrar" — o que sai é o pixel, não
               a informação. */
            <PillButton
              icon={<Filter aria-hidden="true" className="size-4" />}
              label={messages.calendar.filterSheetTitle}
              hideLabel
              count={countActiveFilters(filters)}
              onClick={() => setSheet('filters')}
            />
          }
          compactActions={
            /* No celular a caixa aparece SEM o bloco de atualizações (§2.2):
               quem abre o painel no ônibus está indo agir, não se atualizar. */
            inboxData && inboxData.pending.length > 0 ? (
              <PillButton
                icon={<Inbox aria-hidden="true" className="size-4" />}
                label={messages.calendar.inboxSheetTitle}
                count={inboxData.pending.length}
                onClick={() => setSheet('inbox')}
              />
            ) : null
          }
        />

        {query.error ? (
          <EmptyState title="Não foi possível carregar" description={toUserMessage(query.error)} />
        ) : null}

        {/* A grade permanece montada durante o carregamento: desmontá-la faria a
            tela pular de altura a cada troca de mês, e o FullCalendar perderia a
            posição. O indicador só aparece na primeira carga. */}
        {query.isPending ? <LoadingState label="Carregando o calendário…" compact /> : null}

        <div
          ref={gridMotionRef}
          className={cn(motion.className, 'lg:flex lg:min-h-0 lg:flex-1 lg:flex-col')}
        >
          <ProjectCalendar
            ref={grid}
            items={filtered}
            view={view}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
            onOpenItem={openItem}
            onDayContextMenu={
              canWrite ? (day, position) => setMenu({ day, x: position.x, y: position.y }) : null
            }
            onPeriodChange={handlePeriodChange}
            isNarrow={isNarrow}
            fillHeight={isWide}
            initialDay={anchor}
          />
        </div>
      </section>

      {/*
        A coluna da esquerda. **Quem rola é ela, nunca a página** (§6.2) — é o que
        mantém a grade inteira na tela enquanto a pessoa desce uma lista de oito
        itens do dia.
      */}
      <aside className="mt-5 shrink-0 space-y-5 lg:order-1 lg:mt-0 lg:w-[208px] lg:overflow-y-auto lg:pr-5">
        {hasInbox ? (
          <>
            {/* No celular a caixa vive na folha, não na coluna. */}
            <div className="hidden lg:block">
              <CalendarInbox data={inboxData} />
            </div>
            {/* A régua fina que separa os dois blocos — e ela some junto com a
                caixa, senão sobra um traço solto acima da lista do dia. */}
            <div aria-hidden="true" className="hidden border-t border-border lg:block" />
          </>
        ) : null}

        <div ref={dayMotionRef} className="animate-rise-in">
          <DayAgendaList day={selectedDay} items={dayItems} onOpenItem={showItem} />
        </div>
      </aside>

      {menu ? (
        <DayContextMenu
          day={menu.day}
          position={{ x: menu.x, y: menu.y }}
          onClose={() => setMenu(null)}
        />
      ) : null}

      <EventSheet item={sheetItem} onClose={() => setSheetItem(null)} onShow={showItem} />

      <CalendarFilterSheet
        open={sheet === 'filters'}
        onClose={() => setSheet(null)}
        filters={filters}
        onChange={handleFilters}
        options={options}
      />

      <Sheet
        open={sheet === 'inbox'}
        onClose={() => setSheet(null)}
        title={messages.calendar.inboxSheetTitle}
      >
        {inboxData ? (
          <CalendarInbox data={{ ...inboxData, updates: [] }} />
        ) : null}
      </Sheet>
    </div>
  );
}

/**
 * O botão que abre uma folha, com o número do que está lá dentro.
 *
 * O contador não é enfeite: com as escolhas escondidas dentro da folha, uma
 * grade filtrada é indistinguível de uma grade vazia, e a pessoa conclui que o
 * mês não tem nada marcado. É também por causa dele que `hideLabel` esconde a
 * PALAVRA e nunca o número — um ícone mudo não diz que há recorte ativo.
 */
function PillButton({
  icon,
  label,
  hideLabel = false,
  count,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  /** Some com o rótulo visível; `aria-label` e `title` continuam dizendo o nome. */
  hideLabel?: boolean;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={hideLabel ? label : undefined}
      title={hideLabel ? label : undefined}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-border bg-card text-sm font-medium',
        'transition-[background-color,transform] duration-fast ease-out active:scale-[0.97]',
        'hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        // Sem rótulo o botão vira redondo de 2.25rem — alvo de toque inteiro,
        // e não um retângulo estreito do tamanho do ícone. Com o contador ao
        // lado ele volta a ser pílula, senão o número não caberia.
        hideLabel ? (count > 0 ? 'h-9 px-2.5' : 'size-9 justify-center') : 'px-3 py-1.5',
      )}
    >
      {icon}
      {hideLabel ? <span className="sr-only">{label}</span> : label}
      {count > 0 ? (
        <span className="tabular rounded-full bg-primary/10 px-1.5 text-xs font-semibold text-primary">
          {count}
        </span>
      ) : null}
    </button>
  );
}

/**
 * A lista de um parâmetro repetido — e `undefined` quando não veio nenhum.
 *
 * Lista vazia e ausente dizem a mesma coisa ("não recortei esta dimensão"), e
 * escolher `undefined` para as duas é o que impede a URL de guardar `?espaco=`
 * pendurado depois de a pessoa desmarcar a última caixa.
 */
function listOf<T extends string>(values: readonly T[]): readonly T[] | undefined {
  return values.length > 0 ? values : undefined;
}

/** O primeiro dia do período visível — para a vista de Semana, um domingo. */
function firstVisibleDay(info: { anchor: string; start: string }): string {
  return info.anchor > info.start ? info.anchor : info.start;
}
